import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Scan, Package, CheckCircle2, AlertCircle, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../LanguageContext';

const InvoiceScanModal = ({ products, onAddItem, onClose }) => {
  const { t } = useTranslation();
  const [barcode, setBarcode] = useState('');
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [flashItem, setFlashItem] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [recentItems, setRecentItems] = useState([]);
  const inputRef = useRef(null);
  const countdownRef = useRef(null);
  const addedRef = useRef(false);

  useEffect(() => {
    const focusInput = () => {
      console.log("test focus");
      
      if (
        inputRef.current &&
        document.activeElement !== inputRef.current
      ) {
        inputRef.current.focus();
      }
    };

    // Focus immediately
    focusInput();

    // Check every 2 seconds
    const interval = setInterval(focusInput, 2000);

    return () => clearInterval(interval);
  }, []);

  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const processScan = useCallback((code) => {
    setScanning(true);
    setError(null);

    setTimeout(() => {
      let matchedProduct = null;
      let matchedVariant = null;

      for (const product of products) {
        if (product.barcode === code) {
          matchedProduct = product;
          break;
        }
        if (product.variants) {
          for (const variant of product.variants) {
            if (variant.barcode === code) {
              matchedProduct = product;
              matchedVariant = variant;
              break;
            }
          }
          if (matchedProduct) break;
        }
      }

      if (!matchedProduct) {
        setError(t('invoices.barcode_not_found'));
        toast.error(t('invoices.barcode_not_found'));
        setBarcode('');
        setScanning(false);
        focusInput();
        return;
      }

      const variantReference = matchedVariant
        ? (matchedVariant.color && matchedVariant.size
          ? `${matchedVariant.color} / ${matchedVariant.size}`
          : matchedVariant.color || matchedVariant.size || '')
        : '';

      const item = {
        id: Date.now(),
        product_id: matchedProduct.id,
        product_variant_id: matchedVariant ? matchedVariant.id : '',
        product_name: matchedProduct.name,
        variant_reference: variantReference,
        quantity: 1,
        unit_price: matchedProduct.price,
        line_total: matchedProduct.price,
        stock: matchedVariant?.stock ?? matchedProduct.qty ?? 0,
      };

      setFlashItem(item);
      setCountdown(3);
      setBarcode('');
      setScanning(false);
    }, 150);
  }, [products, t, focusInput]);

  useEffect(() => {
    if (!flashItem) return;
    addedRef.current = false;
  }, [flashItem]);

  useEffect(() => {
    if (!flashItem || addedRef.current) return;

    if (countdown <= 0) {
      addedRef.current = true;
      onAddItem({
        product_id: flashItem.product_id,
        product_variant_id: flashItem.product_variant_id,
        product_name: flashItem.product_name,
        variant_reference: flashItem.variant_reference,
        quantity: flashItem.quantity,
        unit_price: flashItem.unit_price,
        line_total: flashItem.line_total,
      });
      setRecentItems((prev) => [flashItem, ...prev].slice(0, 20));
      toast.success(`${flashItem.product_name}${flashItem.variant_reference ? ' — ' + flashItem.variant_reference : ''} ${t('invoices.item_added')}`);
      setFlashItem(null);
      setCountdown(0);
      focusInput();
      return;
    }

    countdownRef.current = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(countdownRef.current);
  }, [flashItem, countdown, onAddItem, t, focusInput]);

  const handleScan = (e) => {
    e.preventDefault();
    const code = barcode.trim();
    if (!code) return;
    processScan(code);
  };

  const handleClose = () => {
    clearTimeout(countdownRef.current);
    onClose();
  };

  const totalRecent = recentItems.reduce((sum, item) => sum + item.line_total, 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col transform transition-all scale-100 max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-black text-white rounded-xl">
              <Scan className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('invoices.scan_title')}</h2>
              <p className="text-xs text-gray-500">{t('invoices.scan_subtitle')}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6  space-y-4 overflow-y-auto flex-1">
          {/* Scan Input */}
          <form onSubmit={handleScan} className="absolute top-[-100%] opacity-1">
            <input
              ref={inputRef}
              type="text"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder={t('invoices.scan_now_placeholder')}
              className="w-full px-4 py-4 pr-12 text-lg font-mono border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-black dark:focus:border-white focus:ring-0 transition-all bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              autoFocus
            />
            <button 
              type="submit"
              disabled={scanning || !barcode.trim()}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black dark:bg-white text-white dark:text-black rounded-lg disabled:opacity-30 transition-all hover:scale-105 active:scale-95"
            >
              {scanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Scan className="w-5 h-5" />}
            </button>
          </form>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-xl animate-in slide-in-from-top-2 duration-200">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
              <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Flash Card - Product displayed for 3 seconds */}
          {flashItem && (
            <div className="relative overflow-hidden rounded-2xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-5 animate-in zoom-in-95 duration-200">
              {/* Progress bar */}
              <div className="absolute bottom-0 left-0 h-1 bg-green-500 transition-all duration-1000 ease-linear" style={{ width: `${(countdown / 3) * 100}%` }} />
              
              <div className="flex items-center gap-2 text-xs font-bold text-green-600 dark:text-green-400 uppercase tracking-wider mb-3">
                <Zap className="w-4 h-4" />
                {t('invoices.product_found')} — {countdown}s
              </div>
              
              <div className="flex items-start gap-4">
                <div className="p-3 bg-white dark:bg-gray-900 rounded-xl shadow-sm shrink-0">
                  <Package className="w-8 h-8 text-green-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
                    {flashItem.product_name}
                  </h3>
                  {flashItem.variant_reference && (
                    <p className="text-sm text-blue-600 font-medium mt-1">
                      {flashItem.variant_reference}
                    </p>
                  )}
                  <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-[10px] font-bold text-gray-400 uppercase">{t('invoices.quantity')}</span>
                      <p className="font-bold text-gray-900 dark:text-gray-100">{flashItem.quantity}</p>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-gray-400 uppercase">{t('invoices.unit_price')}</span>
                      <p className="font-bold text-gray-900 dark:text-gray-100">{Number(flashItem.unit_price).toFixed(2)}</p>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-gray-400 uppercase">{t('invoices.stock')}</span>
                      <p className="font-bold text-gray-900 dark:text-gray-100">{flashItem.stock}</p>
                    </div>
                  </div>
                </div>
                <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0 animate-bounce" />
              </div>
            </div>
          )}

          {/* Recent items added */}
          {recentItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                {t('invoices.recently_added')} ({recentItems.length})
              </div>
              {recentItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 text-sm"
                >
                  <Package className="w-4 h-4 text-gray-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-gray-900 dark:text-gray-100 truncate block">{item.product_name}</span>
                    {item.variant_reference && (
                      <span className="text-xs text-blue-600">{item.variant_reference}</span>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-xs text-gray-500">x{item.quantity}</span>
                    <span className="block font-bold text-gray-900 dark:text-gray-100">{Number(item.line_total).toFixed(2)}</span>
                  </div>
                </div>
              ))}
              <div className="text-right text-sm font-bold text-gray-900 dark:text-gray-100 pt-2 border-t border-gray-200 dark:border-gray-700">
                {t('invoices.subtotal')}: {totalRecent}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!flashItem && recentItems.length === 0 && (
            <div className="py-12 flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-gray-300 dark:text-gray-600 animate-pulse">
                <Scan className="w-10 h-10" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('invoices.scan_ready')}</p>
                <p className="text-xs text-gray-400 max-w-[250px]">{t('invoices.scan_ready_desc')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-between shrink-0">
          <p className="text-[10px] text-gray-400 font-medium uppercase tracking-[2px]">
            {t('invoices.scan_footer')}
          </p>
          {recentItems.length > 0 && (
            <span className="text-sm font-bold text-green-600 dark:text-green-400">
              ✓ {recentItems.length} {t('invoices.items_added_success')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceScanModal;
